import {castDraft, produce} from 'immer';

import type {AgentChatAction} from './actions';
import {applyRuntimeEventToThread} from './eventReducer';
import {addMessageNode, switchBranch} from './messageTree';
import {createRunDisplayNameKey} from './runDisplayNameKey';
import type {AgentChatState} from './thread';
import {
  createDefaultState,
  createEmptyThread,
  createThreadFromSummary,
} from './thread';

export function agentChatReducer(
  state: AgentChatState = createDefaultState(),
  action: AgentChatAction
): AgentChatState {
  return produce(state, draft => {
    switch (action.type) {
      case 'createThread': {
        const thread = createEmptyThread(action.id, action.timestamp);
        // NOTE: Use 'castDraft' to avoid "excessively deep" errors with
        // 'WritableDraft' inference (due to 'ReadonlyJSONValue').
        draft.threads[action.id] = castDraft(thread);
        draft.activeThreadId = action.id;
        draft.activeThreadLoadFailure = null;
        break;
      }

      case 'deleteThread': {
        delete draft.threads[action.threadId];
        delete draft.pendingRenames[action.threadId];
        draft.pendingDeletes.delete(action.threadId);
        draft.deletedThreadIds.add(action.threadId);
        if (draft.activeThreadId === action.threadId) {
          draft.activeThreadId = null;
        }
        break;
      }

      case 'setActiveThread': {
        // Activating a thread clears any prior load failure; this is the only
        // reset for the already-loaded path (no 'beginLoadThread' will fire).
        draft.activeThreadId = action.threadId;
        draft.activeThreadLoadFailure = null;
        break;
      }

      case 'renameThread': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          // Skip stale rename responses; either 'mergeThreadSummaries' or
          // 'loadThread' already refreshed the title.
          if (action.serverUpdatedAt < thread.serverUpdatedAt) {
            break;
          }
          thread.title = action.title;
          // Use a maximum so a rename during active streaming doesn't demote
          // the thread in the chat history's sort order.
          thread.updatedAt = Math.max(thread.updatedAt, action.timestamp);
          thread.serverUpdatedAt = action.serverUpdatedAt;
        }
        break;
      }

      case 'loadThread': {
        draft.threads[action.thread.id] = castDraft(action.thread);
        if (draft.activeThreadId === action.thread.id) {
          draft.isActiveThreadLoading = false;
        }
        break;
      }

      case 'beginLoadThread': {
        draft.activeThreadId = action.threadId;
        draft.isActiveThreadLoading = true;
        draft.activeThreadLoadFailure = null;
        break;
      }

      case 'failLoadThread': {
        // Record the failure only if its thread is still active. A load the
        // user switched away from can't fail late and hit the wrong thread.
        if (draft.activeThreadId === action.threadId) {
          draft.isActiveThreadLoading = false;
          draft.activeThreadLoadFailure = action.failure;
        }
        break;
      }

      case 'addUserMessage': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          addMessageNode(thread.messageTree, action.parentId, action.message);
          thread.updatedAt = action.timestamp;
        }
        break;
      }

      case 'switchBranch': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          switchBranch(thread.messageTree, action.targetNodeId);
          thread.updatedAt = action.timestamp;
        }
        break;
      }

      case 'applyRuntimeEvent': {
        const thread = draft.threads[action.threadId];
        if (!thread) {
          break;
        }

        // Drop events from runs that have already ended. After an optimistic
        // cancel (or normal completion) records an outcome, every subsequent
        // event tagged with that `runId` is stale and must be discarded.
        if (action.runId in thread.runOutcomes) {
          break;
        }

        applyRuntimeEventToThread(thread, action.event, action.timestamp);
        break;
      }

      case 'setThreadStatus': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          thread.status = action.status;
          thread.updatedAt = action.timestamp;
        }
        break;
      }

      case 'setThreadError': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          thread.error = action.error;
          thread.updatedAt = action.timestamp;
        }
        break;
      }

      case 'resolveRunDisplayNames': {
        const thread = draft.threads[action.threadId];
        if (!thread) {
          break;
        }

        for (const node of Object.values(thread.messageTree.nodes)) {
          for (const part of node.content) {
            if (part.type !== 'wandb-run') {
              continue;
            }

            const key = createRunDisplayNameKey(part);
            const result = action.results[key];
            if (!result) {
              continue;
            }

            if (result.status === 'resolved') {
              part.runDisplayName = result.displayName;
              part.runDisplayNameStatus = 'resolved';
            } else {
              delete part.runDisplayName;
              part.runDisplayNameStatus = result.status; // 'not-found' | 'error'
            }
          }
        }
        break;
      }

      case 'beginRename': {
        draft.pendingRenames[action.threadId] = {title: action.title};
        break;
      }

      case 'endRename': {
        delete draft.pendingRenames[action.threadId];
        break;
      }

      case 'beginDelete': {
        draft.pendingDeletes.add(action.threadId);
        if (draft.activeThreadId === action.threadId) {
          draft.activeThreadId = null;
        }
        break;
      }

      case 'endDelete': {
        draft.pendingDeletes.delete(action.threadId);
        break;
      }

      case 'mergeThreadSummaries': {
        for (const summary of action.summaries) {
          // Skip ids the user has just deleted (or is deleting); a list
          // refetch in flight when the delete fired could otherwise
          // re-add the row.
          if (
            state.pendingDeletes.has(summary.id) ||
            state.deletedThreadIds.has(summary.id)
          ) {
            continue;
          }

          const existing = draft.threads[summary.id];
          if (!existing) {
            draft.threads[summary.id] = castDraft(
              createThreadFromSummary(summary)
            );
            continue;
          }

          // Skip stale refetches. `updatedAt` would conflate server time with
          // local streaming bumps; `serverUpdatedAt` is server-only.
          if (summary.updatedAt < existing.serverUpdatedAt) {
            continue;
          }

          existing.title = summary.title;
          existing.promptSnippet = summary.promptSnippet;
          existing.serverUpdatedAt = summary.updatedAt;
          // Use a maximum so a refresh during active streaming doesn't demote
          // the thread in the chat history's sort order.
          existing.updatedAt = Math.max(existing.updatedAt, summary.updatedAt);
          // Only refresh projects on thread summaries; loaded threads own the
          // full derivation.
          if (existing.loadStatus === 'summary') {
            existing.projects = summary.projects;
          }
        }
        break;
      }

      default: {
        const _exhaustive: never = action;
        void _exhaustive;
        break;
      }
    }
  });
}
